import {openDeleteDialog, showNotification, Tag, Textarea, Tooltip} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';

import {useRef, useState} from 'react';

import {isNil, isNotEmpty, isNotNil} from 'ramda';

import {
  RecordAuditNoteResponseBody,
  useCreateRecordAuditNoteMutation,
  useDeleteRecordAuditNoteMutation,
  useGetRecordAuditNotesQuery,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {useOnMount} from 'shared/web';

const PILL_LABEL_MAX_CHARS = 40;
const COMMIT_DEBOUNCE_MS = 600;

interface RecordAuditTaskNoteProps {
  value: string | null | undefined;
  onChange: (next: string | null) => void;
  isDisabled?: boolean;
}

export function RecordAuditTaskNote(props: RecordAuditTaskNoteProps) {
  const {data: auditNotes} = useGetRecordAuditNotesQuery(undefined, {skip: props.isDisabled});
  const [createAuditNote] = useCreateRecordAuditNoteMutation();
  const [deleteAuditNote] = useDeleteRecordAuditNoteMutation();

  // Local mirror of the textarea value. Decouples keystrokes from the cache
  // round-trip so the input never snaps back mid-typing when the audit query
  // refetches after a PATCH. Resyncs from the prop when the upstream changes
  // (BE refetch with a different note, or a different task mounting).
  const [localValue, setLocalValue] = useState<string | null | undefined>(props.value);
  const [lastSeenValue, setLastSeenValue] = useState(props.value);
  if (lastSeenValue !== props.value) {
    setLastSeenValue(props.value);
    setLocalValue(props.value);
  }

  // Inline debounce so unmount can flush the pending commit; the shared
  // useDebouncedCallback has no flush()/cancel(). Refs are written only from
  // event handlers and the unmount cleanup — never during render.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flushRef = useRef<(() => void) | null>(null);

  const scheduleCommit = (next: string | null) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    const fire = () => {
      timerRef.current = null;
      flushRef.current = null;
      props.onChange(next);
    };
    flushRef.current = fire;
    timerRef.current = setTimeout(fire, COMMIT_DEBOUNCE_MS);
  };

  // Flush a pending debounce on unmount so navigating away within the 600 ms
  // window doesn't silently drop the user's last keystrokes.
  useOnMount(() => () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    flushRef.current?.();
  });

  const handleTextareaChange = (next: string | null) => {
    const normalized = next === '' || isNil(next) ? null : next;
    setLocalValue(normalized);
    scheduleCommit(normalized);
  };

  const handlePillClick = (text: string) => {
    setLocalValue(text);
    scheduleCommit(text);
  };

  const handleDeleteAuditNote = (note: RecordAuditNoteResponseBody) => {
    openDeleteDialog({
      text: i18n.t('page.checklistAudits.form.deleteNoteTemplateConfirm'),
      onConfirm: () => {
        deleteAuditNote({noteId: note.id})
          .unwrap()
          .then(() =>
            showNotification.success(i18n.t('page.checklistAudits.form.deleteNoteTemplateSuccess'))
          )
          .catch(handleApiError);
      },
    });
  };

  const trimmedValue = localValue?.trim() ?? '';
  const matchesExistingNote = (auditNotes ?? []).some((note) => note.text === trimmedValue);

  const showPills = !props.isDisabled && isNotEmpty(auditNotes ?? []);
  const canSaveAsTemplate = !props.isDisabled && trimmedValue !== '' && !matchesExistingNote;

  const handleSaveAsTemplate = () => {
    createAuditNote({body: {text: trimmedValue}})
      .unwrap()
      .then(() =>
        showNotification.success(i18n.t('page.checklistAudits.form.saveNoteAsTemplateSuccess'))
      )
      .catch(handleApiError);
  };

  return (
    <VStack spacing={2}>
      <Show when={!props.isDisabled || isNotNil(localValue)}>
        <Box>
          <Textarea
            value={localValue ?? ''}
            placeholder={i18n.t('page.checklistAudits.form.notePlaceholder')}
            minRows={2}
            onChange={handleTextareaChange}
            isDisabled={props.isDisabled}
          />
        </Box>
      </Show>
      {/* Hint + template chips on one wrapped row, with "Save as template"
          rendered as the last chip-style label after the existing templates. */}
      <Show when={showPills || canSaveAsTemplate}>
        <HStack spacing={2} wrap align="center">
          <Show when={showPills}>
            <Text size="xSmall" color="tertiary">
              {`${i18n.t('general.labels.templates').toUpperCase()}:`}
            </Text>
          </Show>
          {auditNotes?.map((note) => (
            <RecordAuditNotePill
              key={note.id}
              note={note}
              onClick={() => handlePillClick(note.text)}
              onDelete={() => handleDeleteAuditNote(note)}
            />
          ))}
          <Show when={canSaveAsTemplate}>
            <Tag
              label={i18n.t('page.checklistAudits.form.saveNoteAsTemplate')}
              size="default"
              colorScheme="neutral"
              isSubtle
              onClick={handleSaveAsTemplate}
            />
          </Show>
        </HStack>
      </Show>
    </VStack>
  );
}

interface RecordAuditNotePillProps {
  note: RecordAuditNoteResponseBody;
  onClick: () => void;
  onDelete: () => void;
}

function RecordAuditNotePill(props: RecordAuditNotePillProps) {
  const isTruncated = props.note.text.length > PILL_LABEL_MAX_CHARS;
  const label = isTruncated
    ? `${props.note.text.slice(0, PILL_LABEL_MAX_CHARS)}…`
    : props.note.text;

  return (
    <Tooltip
      label={isTruncated ? props.note.text : undefined}
      placement="top"
      overflowWrap="anywhere"
    >
      <Tag
        label={label}
        size="default"
        colorScheme="blue"
        isSubtle
        onClick={props.onClick}
        onDelete={props.onDelete}
      />
    </Tooltip>
  );
}
